import AuthConstants from './AuthConsts';
import { ServerEndpoint } from '../endpoints.ts';
import * as actionLoading from '../Loading/LoadingActions';
import * as actionToast from '../Toast/ToastActions';
import { setAuthToken } from '../../Services/InterceptorService';
import { post, get } from '../../Services/ApiService';
import jwt_decode from 'jwt-decode';


// ─── Session ──────────────────────────────────────────────────────────────────

// Reads from whichever storage has the token
const authGet  = (key) => localStorage.getItem(key) || sessionStorage.getItem(key);
// Writes to the right storage based on rememberMe flag
const authSet  = (rememberMe, items) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    Object.entries(items).forEach(([k, v]) => storage.setItem(k, v));
};
// Clears from both (logout always clears everything)
const authClear = (...keys) => keys.forEach(k => { localStorage.removeItem(k); sessionStorage.removeItem(k); });

export const isLoggedIn = () => async (dispatch) => {
    const token = authGet('token');
    if (!token) return; // sessionChecked already true in reducer when no token

    try {
        const role = authGet('role');
        setAuthToken(token);
        const checkEndpoint = role === 'user' ? ServerEndpoint.USER_CHECK : ServerEndpoint.LOGIN_CHECK;
        const response = await get(checkEndpoint, true);
        const content = response.data;
        if (content.status) {
            dispatch({ type: AuthConstants.CHECK_LOGIN_SUCCESS, payload: content });
        } else {
            authClear('token', 'role');
            dispatch({ type: AuthConstants.SESSION_CHECKED });
        }
    } catch (error) {
        // Only clear the token if the server explicitly rejected it (401/403).
        // Network errors or 5xx should not sign the user out — they may be transient.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
            authClear('token', 'role');
        }
        dispatch({ type: AuthConstants.SESSION_CHECKED });
    }
}

export const logout = () => async (dispatch) => {
    dispatch(actionLoading.setLoading('Logging Out...'));
    setTimeout(() => {
        dispatch({ type: AuthConstants.LOGOUT });
        setAuthToken();
        authClear('token', 'email', 'name', 'role');
        dispatch(actionLoading.disableLoading());
    }, 2000);
}

export const redirectScreen = (path) => async (dispatch) => {
    dispatch({ type: AuthConstants.REDIRECT_SCREEN, payload: { currentRoute: path } });
}

export const changeTheme = (theme) => async (dispatch) => {
    dispatch({ type: AuthConstants.CHANGE_THEME, payload: { theme } });
}


// ─── Admin Login ──────────────────────────────────────────────────────────────

// Unified login: tries admin endpoint first, falls back to customer.
// Role detection happens entirely server-side — no email comparison on the client.
export const loginUnified = (email, password, rememberMe = true) => async (dispatch) => {
    dispatch(actionLoading.setLoading('Logging in...'));

    try {
        // [0] Try admin login — backend validates against env credentials
        const adminRes = await post(ServerEndpoint.LOGIN, { email, password });
        const adminContent = adminRes.data;
        if (adminContent.status) {
            if (adminContent.data?.requiresOtp) {
                dispatch(actionLoading.disableLoading());
                return { requiresOtp: true };
            }
            // Admin always remembers (admin sessions are managed separately)
            authSet(true, { email: adminContent.data.user.email, name: adminContent.data.user.name || '', token: adminContent.data.token, role: 'admin' });
            setAuthToken(adminContent.data.token);
            dispatch({ type: AuthConstants.LOGIN_SUCCESS, payload: adminContent });
            dispatch(actionToast.setToast('success', 'Welcome, Admin'));
            dispatch(actionLoading.disableLoading());
            return;
        }
    } catch (_) { /* not admin — try customer */ }

    try {
        // [1] Fallback: customer login
        const userRes = await post(ServerEndpoint.USER_LOGIN, { email, password });
        const userContent = userRes.data;
        if (userContent.status) {
            authSet(rememberMe, { email: userContent.data.user.email, name: userContent.data.user.name || '', token: userContent.data.token, role: 'user' });
            setAuthToken(userContent.data.token);
            dispatch({ type: AuthConstants.USER_LOGIN_SUCCESS, payload: userContent });
            dispatch(actionToast.setToast('success', 'Welcome back!'));
            dispatch(actionLoading.disableLoading());
            return;
        }
        // Server returned 200 but status:false — extract message
        dispatch(actionToast.setToast('error', userContent.message || 'Invalid email or password'));
    } catch (err) {
        // HTTP 4xx/5xx — extract message from response body
        const serverMsg = err?.response?.data?.message;
        const status    = err?.response?.status;
        if (status === 403) {
            dispatch(actionToast.setToast('error', serverMsg || 'Please verify your email before logging in'));
        } else if (status === 401) {
            dispatch(actionToast.setToast('error', serverMsg || 'Invalid email or password'));
        } else {
            dispatch(actionToast.setToast('error', serverMsg || 'Login failed. Please try again.'));
        }
    }

    dispatch(actionLoading.disableLoading());
};


// ─── Google Auth ─────────────────────────────────────────────────────────────

export const handleLoginWithGoogle = (res) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Logging in...'));
        const credentials = jwt_decode(res.credential);
        const response = await post(ServerEndpoint.LOGIN_WITH_GOOGLE, { email: credentials.email, name: credentials.name });
        const content = response.data;
        if (content.status) {
            localStorage.setItem('email', content.data.user.email);
            localStorage.setItem('name', content.data.user.name || '');
            localStorage.setItem('token', content.data.token);
            localStorage.setItem('role', 'user');
            setAuthToken(content.data.token);
            dispatch({ type: AuthConstants.USER_LOGIN_SUCCESS, payload: content });
            dispatch(actionToast.setToast('success', 'Welcome!'));
        } else {
            dispatch(actionToast.setToast('error', content.message || 'Google login failed'));
        }
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Google login failed'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};


// ─── User Auth ────────────────────────────────────────────────────────────────

export const loginUser = (email, password) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Logging in...'));
        const response = await post(ServerEndpoint.USER_LOGIN, { email, password }, true);
        const content = response.data;
        if (content.status) {
            localStorage.setItem('email', content.data.user.email);
            localStorage.setItem('name', content.data.user.name || '');
            localStorage.setItem('token', content.data.token);
            localStorage.setItem('role', 'user');
            setAuthToken(content.data.token);
            dispatch({ type: AuthConstants.USER_LOGIN_SUCCESS, payload: content });
            dispatch(actionToast.setToast('success', 'Welcome back!'));
        } else {
            dispatch(actionToast.setToast('error', content.message || 'Login failed'));
        }
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Login failed'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
}

export const forgotPassword = (email) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Sending reset email...'));
        const res = await post(ServerEndpoint.USER_FORGOT_PASSWORD, { email });
        if (res.data.status) {
            dispatch(actionToast.setToast('success', 'Password reset link sent — check your inbox'));
        } else {
            dispatch(actionToast.setToast('error', res.data.message || 'Could not send reset email'));
        }
    } catch (err) {
        const msg = err?.response?.data?.message;
        dispatch(actionToast.setToast('error', msg || 'Could not send reset email'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const resetPassword = (token, newPassword) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Resetting password...'));
        const res = await post(ServerEndpoint.USER_RESET_PASSWORD, { token, newPassword });
        if (res.data.status) {
            dispatch(actionToast.setToast('success', 'Password updated — you can now sign in'));
            return true;
        } else {
            dispatch(actionToast.setToast('error', res.data.message || 'Reset failed'));
            return false;
        }
    } catch (err) {
        const msg = err?.response?.data?.message;
        dispatch(actionToast.setToast('error', msg || 'Reset link is invalid or expired'));
        return false;
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const registerUser = (name, email, password, smsConsent = false, emailConsent = false) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Creating your account...'));
        const response = await post(ServerEndpoint.USER_REGISTER, { name, email, password, smsConsent, emailConsent }, true);
        const content = response.data;
        if (content.status) {
            localStorage.setItem('email', content.data.user.email);
            localStorage.setItem('name', content.data.user.name || '');
            localStorage.setItem('token', content.data.token);
            localStorage.setItem('role', 'user');
            setAuthToken(content.data.token);
            dispatch({ type: AuthConstants.USER_LOGIN_SUCCESS, payload: content });
            dispatch(actionToast.setToast('success', 'Account created successfully!'));
        } else {
            dispatch(actionToast.setToast('error', content.message || 'Registration failed'));
        }
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Registration failed'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
}
